// src/components/common/DurationPicker.tsx
// 時間長度選擇器 - 用於 Duration/EstimatedTime 欄位

import React, { useState, useEffect } from 'react';
import PickerShell from './PickerShell';
import DurationSelect from './DurationSelect';

interface DurationPickerProps {
  /** 當前值（分鐘數） */
  currentMinutes?: number;

  /** 選擇回調 - 返回分鐘數或 undefined（清除） */
  onSelect: (minutes: number | undefined) => void;

  /** 關閉回調 */
  onClose: () => void;
}

interface QuickOption {
  minutes: number;
  label: string;
}

const QUICK_OPTIONS: QuickOption[] = [
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
];

const minutesToHoursMinutes = (totalMinutes: number | undefined): { hours: string; minutes: string } => {
  if (totalMinutes === undefined || totalMinutes === 0) {
    return { hours: '', minutes: '' };
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return {
    hours: hours > 0 ? hours.toString() : '',
    minutes: minutes > 0 ? minutes.toString().padStart(2, '0') : '',
  };
};

const hoursMinutesToMinutes = (hours: string, minutes: string): number | undefined => {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  const total = h * 60 + m;
  return total > 0 ? total : undefined;
};

/**
 * 時間長度選擇器
 * 提供快捷選項和自訂時間輸入
 * 有自訂輸入框，所以底部有 Done 按鈕確認
 */
const DurationPicker: React.FC<DurationPickerProps> = ({
  currentMinutes,
  onSelect,
  onClose,
}) => {
  const initial = minutesToHoursMinutes(currentMinutes);
  const [hours, setHours] = useState(initial.hours);
  const [minutes, setMinutes] = useState(initial.minutes);

  useEffect(() => {
    const { hours: h, minutes: m } = minutesToHoursMinutes(currentMinutes);
    setHours(h);
    setMinutes(m);
  }, [currentMinutes]);

  const handleQuickOptionClick = (optionMinutes: number) => {
    onSelect(optionMinutes);
    onClose();
  };

  const handleDone = () => {
    onSelect(hoursMinutesToMinutes(hours, minutes));
    onClose();
  };

  const handleClear = () => {
    onSelect(undefined);
    onClose();
  };

  return (
    <PickerShell onClose={onClose}>
      {/* Quick Options */}
      <div className="px-4 py-4 border-b border-gray-100">
        <div className="flex flex-wrap gap-2">
          {QUICK_OPTIONS.map((option) => {
            const isSelected = currentMinutes === option.minutes;
            return (
              <button
                key={option.minutes}
                onClick={() => handleQuickOptionClick(option.minutes)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-xl border-2
                  transition-all duration-150
                  ${isSelected
                    ? 'bg-sky-50 text-sky-700 border-sky-200'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-sky-200 hover:bg-sky-50'
                  }
                `}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Duration Input */}
      <div className="px-4 py-4">
        <p className="text-sm text-gray-500 mb-3">Or set custom duration:</p>
        <DurationSelect
          hours={hours}
          minutes={minutes}
          onHoursChange={setHours}
          onMinutesChange={setMinutes}
        />
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex gap-2">
        {currentMinutes !== undefined && (
          <button
            onClick={handleClear}
            className="flex-1 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700
                       hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
          >
            Clear
          </button>
        )}
        <button
          onClick={handleDone}
          className="flex-1 py-2.5 text-sm font-medium text-white bg-sky-600
                     hover:bg-sky-700 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    </PickerShell>
  );
};

export default DurationPicker;
